import React from "react";
import { COLORS } from "../theme/colors";
import { Button } from "./ui";
import Icon from "./Icon";

// I² Visual Identity v1, Modernization addendum M2.1 (2026-04-29).
//
// Standardized page-header primitive. Every top-level tab today
// implements its own header inline (title text + button cluster +
// search box) — different shapes per tab, no consistent rhythm. This
// component pins the shape so every tab reads as the same product.
//
// Slots:
//   title            — H1, Outfit 24px bold (required)
//   subtitle         — string OR ReactNode, 13px textMuted (optional)
//   primaryAction    — { label, onClick, iconName?, disabled?, loading?, variant? }
//   secondaryActions — [{ label, onClick, iconName?, variant? }]
//   filters          — ReactNode (search box + filter chips, etc.)
//   breadcrumb       — [{ label, onClick? }] — future use; v1 ignores it
//
// Layout:
//   Row 1: title block (left) + actions cluster (right, primary on far right)
//   Row 2 (optional): subtitle
//   Row 3 (optional): filters slot
//
// Adoption: M2.2-M2.7 wire each top-level tab to use this component.

export default function PageHeader({
  title,
  subtitle,
  primaryAction,
  secondaryActions,
  filters,
  breadcrumb,    // [{ label, onClick? }] — parent-context trail above the H1
  style,
}) {
  return (
    <div style={{
      marginBottom: 24,
      display: "flex",
      flexDirection: "column",
      gap: 14,
      ...style,
    }}>
      {/* Row 1 — title block + action cluster */}
      <div style={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 16,
        flexWrap: "wrap",
      }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          {breadcrumb && breadcrumb.length > 0 && (
            <nav
              aria-label="Breadcrumb"
              style={{
                fontSize: 12,
                color: COLORS.textMuted,
                marginBottom: 4,
                lineHeight: 1.5,
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 4,
              }}
            >
              {breadcrumb.map((crumb, i) => {
                const isLast = i === breadcrumb.length - 1;
                const isLink = !isLast && typeof crumb.onClick === "function";
                return (
                  <React.Fragment key={i}>
                    {isLink ? (
                      <button
                        type="button"
                        onClick={crumb.onClick}
                        style={{
                          background: "transparent",
                          border: "none",
                          padding: 0,
                          font: "inherit",
                          color: COLORS.textMuted,
                          cursor: "pointer",
                          textDecoration: "none",
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.color = COLORS.text; e.currentTarget.style.textDecoration = "underline"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.color = COLORS.textMuted; e.currentTarget.style.textDecoration = "none"; }}
                      >
                        {crumb.label}
                      </button>
                    ) : (
                      <span style={{ color: isLast ? COLORS.text : COLORS.textMuted, fontWeight: isLast ? 500 : 400 }}>
                        {crumb.label}
                      </span>
                    )}
                    {!isLast && (
                      <span aria-hidden="true" style={{ color: COLORS.textMuted, opacity: 0.6 }}>›</span>
                    )}
                  </React.Fragment>
                );
              })}
            </nav>
          )}
          <h1 style={{
            fontFamily: "'Outfit', 'DM Sans', sans-serif",
            fontSize: 24,
            fontWeight: 700,
            letterSpacing: "-0.02em",
            color: COLORS.text,
            margin: 0,
            lineHeight: 1.2,
          }}>
            {title}
          </h1>
          {subtitle && (
            <div style={{
              fontSize: 13,
              color: COLORS.textMuted,
              marginTop: 4,
              lineHeight: 1.5,
            }}>
              {subtitle}
            </div>
          )}
        </div>

        {/* Action cluster — secondary first (left), primary on the far right */}
        {(primaryAction || (secondaryActions && secondaryActions.length > 0)) && (
          <div style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            flexShrink: 0,
            flexWrap: "wrap",
          }}>
            {(secondaryActions || []).map((act, i) => (
              <Button
                key={i}
                variant={act.variant || "secondary"}
                onClick={act.onClick}
                disabled={act.disabled || act.loading}
                style={{ display: "inline-flex", alignItems: "center", gap: 6, ...act.style }}
              >
                {act.iconName && <Icon name={act.iconName} size={14} color="currentColor" />}
                {act.loading ? "…" : act.label}
              </Button>
            ))}
            {primaryAction && (
              <Button
                variant={primaryAction.variant || "primary"}
                onClick={primaryAction.onClick}
                disabled={primaryAction.disabled || primaryAction.loading}
                style={{ display: "inline-flex", alignItems: "center", gap: 6, ...primaryAction.style }}
              >
                {primaryAction.iconName && <Icon name={primaryAction.iconName} size={14} color="currentColor" />}
                {primaryAction.loading ? "…" : primaryAction.label}
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Row 3 — filters slot (search + chips, etc.) */}
      {filters && (
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          flexWrap: "wrap",
        }}>
          {filters}
        </div>
      )}
    </div>
  );
}
